import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Close as CloseIcon,
  Public,
  VisibilityOff,
  Camera as CameraIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { createMemory, updateMemory, getUserMemoryByGame, getUserGameStatus, deleteMemory } from '../services/api';
import InfoTooltip from './common/InfoTooltip';
import StyledTextField from './common/StyledTextField';
import ConfirmDialog from './dialogs/ConfirmDialog';
import Tips from './dialogs/Tips';
import ReviewPreviewDialog from './dialogs/ReviewPreviewDialog';

interface GameMemoryButtonProps {
  gameId: number;
  gameName: string;
  disabled?: boolean;
  onMemoryCreated?: () => void;
}

const GameMemoryButton: React.FC<GameMemoryButtonProps> = ({
  gameId,
  gameName,
  disabled = false,
  onMemoryCreated
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [memoryId, setMemoryId] = useState<number | null>(null);
  const [loadingMemory, setLoadingMemory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gameStatus, setGameStatus] = useState<{
    playStartDate: string | null;
    playEndDate: string | null;
    status: number | null;
  }>({ playStartDate: null, playEndDate: null, status: null });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const reviewFormatTips = (
    <Box>
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mb: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
          lineHeight: 1.2
        }}
      >
        Tips：コードをクリックでクリップボードにコピーできます
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          {
            title: '見出し',
            examples: [
              { syntax: '[h1]ヘッダー[/h1]', result: 'ヘッダー' },
              { syntax: '[h2]ヘッダー[/h2]', result: 'ヘッダー' },
              // { syntax: '[h3]小見出し[/h3]', result: '小見出し' }
            ]
          },
          {
            title: 'テキスト装飾',
            examples: [
              { syntax: '[b]太字[/b]', result: '太字' },
              { syntax: '[i]斜体[/i]', result: '斜体' },
              { syntax: '[s]取り消し線[/s]', result: '取り消し線' },
              { syntax: '[u]下線[/u]', result: '下線' }
            ]
          },
          {
            title: 'リスト',
            examples: [
              { syntax: '[list]\n[*]リスト項目\n[*]リスト項目\n[/list]', result: '箇条書き' },
              { syntax: '[olist]\n[*]リスト項目\n[*]リスト項目\n[/olist]', result: '番号付きリスト' }
            ]
          },
          {
            title: 'その他',
            examples: [
              { syntax: '[quote]引用文[/quote]', result: '引用ブロック' },
              { syntax: '[spoiler]ネタバレ[/spoiler]', result: 'ネタバレ' },
            ]
          }
        ].map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              {section.title}
            </Typography>
            {section.examples.map((example, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1,
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  }
                }}
              >
                <Box
                  component="code"
                  onClick={() => navigator.clipboard.writeText(example.syntax)}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    p: 0.5,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                    }
                  }}
                >
                  {example.syntax}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {example.result}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const handleOpen = async () => {
    setOpen(true);
    setLoadingMemory(true);
    
    try {
      // ゲームステータスと既存のメモリーを並行取得
      const [statusResponse, existingMemory] = await Promise.allSettled([
        getUserGameStatus(gameId),
        getUserMemoryByGame(gameId)
      ]);

      // ゲームステータスの処理
      if (statusResponse.status === 'fulfilled') {
        setGameStatus({
          playStartDate: statusResponse.value.playStartDate,
          playEndDate: statusResponse.value.playEndDate,
          status: statusResponse.value.status
        });
      }

      // 既存メモリーの処理
      if (existingMemory.status === 'fulfilled' && existingMemory.value) {
        // 編集モードで開く
        setIsEditMode(true);
        setMemoryId(existingMemory.value.id);
        setTitle(existingMemory.value.title);
        setContent(existingMemory.value.content);
        setIsPublic(existingMemory.value.is_public);
        setIsAnonymous(existingMemory.value.is_anonymous);
      } else {
        // 新規作成モード
        setIsEditMode(false);
        setMemoryId(null);
        setTitle('');
        setContent('');
        setIsPublic(true);
        setIsAnonymous(false);
      }
    } catch (error) {
      console.error('データの取得に失敗しました:', error);
      // エラーの場合は新規作成モードで開く
      setIsEditMode(false);
      setMemoryId(null);
      setTitle('');
      setContent('');
      setIsPublic(true);
      setIsAnonymous(false);
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTitle('');
    setContent('');
    setIsPublic(true);
    setIsAnonymous(false);
    setIsEditMode(false);
    setMemoryId(null);
    setDeleting(false);
    setOpenDeleteConfirmDialog(false);
    setPreviewOpen(false);
    setGameStatus({ playStartDate: null, playEndDate: null, status: null });
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      return;
    }

    setLoading(true);
    try {
      const memoryData = {
        title: title.trim(),
        content: content.trim(),
        is_public: isPublic,
        is_anonymous: isAnonymous,
      };

      if (isEditMode && memoryId) {
        // 既存のメモリーを更新
        await updateMemory(memoryId, memoryData);
      } else {
        // 新規メモリーを作成
        await createMemory({
          game: gameId,
          ...memoryData,
        });
      }

      handleClose();
      onMemoryCreated?.();
    } catch (error) {
      console.error('メモリーの投稿に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteConfirmDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memoryId) return;

    setDeleting(true);
    try {
      await deleteMemory(memoryId);
      handleClose();
      onMemoryCreated?.(); // リスト更新のため
    } catch (error) {
      console.error('メモリーの削除に失敗しました:', error);
    } finally {
      setDeleting(false);
      setOpenDeleteConfirmDialog(false);
    }
  };

  // ステータスから表示用の日付を取得
  const getDisplayDate = () => {
    if (gameStatus.playEndDate) {
      return gameStatus.playEndDate;
    } else if (gameStatus.playStartDate) {
      return gameStatus.playStartDate;
    }
    return null;
  };

  const getDateSourceText = () => {
    if (gameStatus.playEndDate) {
      return 'プレイ終了日時';
    } else if (gameStatus.playStartDate) {
      return 'プレイ開始日時';
    }
    return null;
  };

  return (
    <>
      <Tooltip title="思い出を記録" arrow>
        <IconButton
          onClick={handleOpen}
          disabled={disabled}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            padding: { xs: '6px 12px', sm: '8px 12px' },
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              borderColor: 'rgba(255, 255, 255, 0.04)',
            }
          }}
        >
          <CameraIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditMode ? <EditIcon /> : <CameraIcon />}
            <Typography variant="h6">
              {isEditMode ? 'メモリーを編集' : 'メモリーを投稿'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isEditMode && (
              <Tooltip title="削除" arrow>
                <IconButton
                  onClick={handleDeleteClick}
                  size="small"
                  disabled={deleting || loadingMemory}
                  sx={{ 
                    color: 'rgba(255, 99, 99, 0.7)',
                    '&:hover': {
                      color: 'rgba(255, 99, 99, 1)',
                      backgroundColor: 'rgba(255, 99, 99, 0.1)'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {gameName}
          </Typography>

          {loadingMemory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                データを読み込み中...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <StyledTextField
                  id="title"
                  label="タイトル（任意）"
                  size="small"
                  value={title}
                  onChange={setTitle}
                  placeholder="タイトルを入力..."
                  multiline={false}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>

                            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                mt: -1
              }}>
                <Button
                  color="primary"
                  size="small"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!content.trim()}
                  sx={{ 
                    fontSize: { xs: "0.55rem", sm: "0.6rem" },
                    minWidth: 'auto',
                    p: '2px 8px'
                  }}
                >
                  プレビュー
                </Button>
                <Tips 
                  title="テキストフォーマット" 
                  content={reviewFormatTips}
                  buttonComponent={
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.6rem" },
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      使用可能フォーマット
                    </Typography>
                  }
                />
              </Box>
              <Box sx={{ mt: 0.25, mb: 2 }}>
                <StyledTextField
                  id="content"
                  label="内容"
                  value={content}
                  onChange={setContent}
                  multiline
                  minRows={fullScreen ? 6 : 4}
                  maxRows={12}
                  placeholder="このゲームの思い出を書いてみましょう"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="プレイ時期"
                  type="date"
                  value={getDisplayDate() || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                    style: { 
                      color: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)'
                    }
                  }}
                  size="small"
                  disabled
                  helperText={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <InfoIcon sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {getDateSourceText() 
                          ? `ゲームステータスの${getDateSourceText()}が適用されます`
                          : 'ゲームステータスに日付が設定されていません'
                        }
                      </Typography>
                    </Box>
                  }
                />
                {!getDisplayDate() && gameStatus.status && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 1,
                      backgroundColor: 'rgba(33, 150, 243, 0.15)',
                      border: '1px solid rgba(33, 150, 243, 0.3)',
                      '& .MuiAlert-message': {
                        color: 'rgba(255, 255, 255, 0.8)'
                      }
                    }}
                  >
                    ゲームステータスでプレイ日時を設定すると、メモリーに反映されます
                  </Alert>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* 公開設定 
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      icon={<VisibilityOff />}
                      checkedIcon={<Public />}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        {isPublic ? '公開' : '非公開'}
                      </Typography>
                      <InfoTooltip title={isPublic ? 'この思い出は他のユーザーに表示されます' : 'この思い出は自分だけに表示されます'} />
                    </Box>
                  }
                />
                */}
                {/* {isPublic && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAnonymous}
                        onChange={(e) => setIsAnonymous(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">メモリーの匿名化</Typography>
                        <InfoTooltip title="プロフィール以外のページ（新着メモリーなど - 今後実装）では匿名として表示されます" />
                      </Box>
                    }
                  />
                )} */}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingMemory || deleting || !content.trim()}
          >
            {loading ? (isEditMode ? '更新中...' : '投稿中...') : (isEditMode ? '更新' : '投稿')}
          </Button>
          <Button 
            onClick={handleClose} 
            color="inherit"
            disabled={loading || loadingMemory || deleting}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteConfirmDialog}
        title="削除の確認"
        message="メモリーを削除しますか？削除すると元に戻すことはできません。"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirmDialog(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />

      <ReviewPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reviewText={content}
      />
    </>
  );
};

export default GameMemoryButton; 